import { appRoutePatterns } from './appRoutePatterns';
import { buildOperationsPlanningHandoff } from './operationsPlanningHandoff';
import { buildImplementHandoff } from './implementHandoff';
import type { FmRouteCatalogData, FmRouteCatalogEntry } from '@/shared/ui/FmRouteCatalog';
import { getRequiredCatalogRoute } from '@/shared/ui/FmRouteCatalog';
import { fmRouteCatalogDefault } from '@/shared/ui/FmRouteCatalogDefault';
import {
  canonicalInternalPath,
  INTERNAL_APP_ORIGIN,
  normalizeInternalAppReturnPath,
} from '@/types/internalNavigation';

export const PROPERTY_SELECTION_TARGET_PARAM = 'nav_target';

export interface CatalogNavigationContext {
  propertyId: string | null;
  returnTo: string;
}

function propertySelectionPath(routeId: string, returnTo: string): string {
  const target = new URL(appRoutePatterns.myForests, INTERNAL_APP_ORIGIN);
  target.searchParams.set(PROPERTY_SELECTION_TARGET_PARAM, routeId);
  const safeReturn = normalizeInternalAppReturnPath(returnTo);
  if (safeReturn) target.searchParams.set('return_to', safeReturn);
  return canonicalInternalPath(target);
}

/**
 * Build the governed chooser handoff for a route that explicitly declares
 * property-selection copy. Unlike ordinary destination resolution, this also
 * supports global property-bearing workspaces whose URL does not require an id
 * but whose active forest must change before the owner returns to the job.
 */
export function resolveCatalogPropertySelectionDestination(
  route: FmRouteCatalogEntry,
  returnTo: string,
): string {
  if (route.placement !== 'menu' || !route.selection) {
    throw new Error(
      `Navigation route "${route.id}" cannot own a property-selection handoff without menu placement and chooser copy.`,
    );
  }
  return propertySelectionPath(route.id, returnTo);
}

/**
 * Resolve one catalog destination. Property jobs never guess an id: without
 * context they enter the governed My Forests chooser carrying the exact job the
 * owner intended to continue.
 */
export function resolveCatalogDestination(
  route: FmRouteCatalogEntry,
  context: CatalogNavigationContext,
): string {
  if (route.placement !== 'menu') {
    throw new Error(
      `Navigation route "${route.id}" is ${route.placement}; only menu destinations can be resolved without their owning workspace context.`,
    );
  }
  if (route.requiredContext === 'none') {
    // A global workspace that owns its forest choice opens on the active
    // forest; a cross-forest workspace (campaign management) stays global.
    const forestParameter = route.id === 'map'
      ? 'property'
      : route.selection && route.propertyContext?.parameter;
    if (forestParameter && context.propertyId) {
      const target = new URL(route.path, INTERNAL_APP_ORIGIN);
      target.searchParams.set(forestParameter, context.propertyId);
      return canonicalInternalPath(target);
    }
    return route.path;
  }
  if (!context.propertyId) {
    return resolveCatalogPropertySelectionDestination(route, context.returnTo);
  }
  if (route.id === 'operations-planning') {
    return buildOperationsPlanningHandoff({
      propertyId: context.propertyId,
      returnTo: context.returnTo,
    });
  }
  if (route.id === 'implement') {
    return buildImplementHandoff({
      propertyId: context.propertyId,
      returnTo: context.returnTo,
    });
  }
  const destination = route.path.replace(':propertyId', encodeURIComponent(context.propertyId));
  if (/:[a-zA-Z_]\w*/.test(destination)) {
    throw new Error(
      `Navigation route "${route.id}" still requires unresolved contextual parameters.`,
    );
  }
  return destination;
}

/**
 * Resolve a destination by its catalog id. Feature surfaces use this helper
 * instead of repeating route lookup or hard-coding a second destination.
 */
export function resolveCatalogRouteDestination(
  catalog: FmRouteCatalogData,
  routeId: string,
  context: CatalogNavigationContext,
): string {
  const route = getRequiredCatalogRoute(catalog, routeId);
  return resolveCatalogDestination(route, context);
}

export function resolveDefaultCatalogRouteDestination(
  routeId: string,
  context: CatalogNavigationContext,
): string {
  return resolveCatalogRouteDestination(fmRouteCatalogDefault, routeId, context);
}

/**
 * Read a property-selection intent only when it names a real menu destination
 * that explicitly owns chooser copy. This includes property-required jobs and
 * global property-bearing workspaces such as Map and Watch, whose ordinary
 * activation remains global while forest switching returns to the same job.
 */
export function getPropertySelectionTarget(
  searchParams: URLSearchParams,
  catalog: FmRouteCatalogData = fmRouteCatalogDefault,
): FmRouteCatalogEntry | null {
  const routeId = searchParams.get(PROPERTY_SELECTION_TARGET_PARAM);
  if (!routeId) return null;
  return (
    catalog.routes.find(
      (route) =>
        route.id === routeId &&
        route.placement === 'menu' &&
        Boolean(route.selection) &&
        route.availability !== 'coming-soon',
    ) ?? null
  );
}
